import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import api from '../utils/api';

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user,    setUser]    = useState(null);
  const [loading, setLoading] = useState(true);
  const initDone = useRef(false);

  useEffect(() => {
    if (initDone.current) return;
    initDone.current = true;

    const token = localStorage.getItem('lr_token');

    // No token at all → not logged in
    if (!token) {
      setLoading(false);
      return;
    }

    // ── FAST PATH ────────────────────────────────────────────────────────
    // After OTP verify, RegisterPage stores user in sessionStorage.
    // On the redirect, read it instantly — no network call, no flash to /login.
    const cached = sessionStorage.getItem('lr_user');
    if (cached) {
      try {
        const parsedUser = JSON.parse(cached);
        if (parsedUser && parsedUser.id) {
          sessionStorage.removeItem('lr_user'); // consume once
          setUser(parsedUser);
          setLoading(false);
          return;
        }
      } catch (e) {
        sessionStorage.removeItem('lr_user');
      }
    }

    // ── NORMAL PATH ──────────────────────────────────────────────────────
    // Validate token on every page refresh (no sessionStorage cache)
    api.get('/auth/me')
      .then(r => {
        if (r.data?.user) {
          setUser(r.data.user);
        } else {
          localStorage.removeItem('lr_token');
        }
      })
      .catch(() => {
        localStorage.removeItem('lr_token');
      })
      .finally(() => {
        setLoading(false);
      });

  }, []);

  const login = useCallback(async (email, password) => {
    const r = await api.post('/auth/login', { email, password });
    if (r.data.token) {
      localStorage.setItem('lr_token', r.data.token);
    }
    if (!r.data.requiresVerification && r.data.user) {
      setUser(r.data.user);
    }
    return r.data;
  }, []);

  const register = useCallback(async (data) => {
    const r = await api.post('/auth/register', data);
    return r.data;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('lr_token');
    sessionStorage.removeItem('lr_user');
    setUser(null);
  }, []);

  const updateUser = useCallback((updated) => {
    setUser(prev => prev ? { ...prev, ...updated } : updated);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
}